import React from 'react'
import { CustomerAddress } from 'lib/types/core'
import { Toast } from 'lib/ui/components'
import { AddressInfo } from './components/AddressInfo'
import { AddressActions } from './components/AddressActions'
import { useDeleteCustomerAddress, useUpdateDefaultShipping, useUpdateDefaultBilling, useCustomer } from 'lib/core/hooks'
import { useSetDefaultShippingAddressOnCart } from 'hooks/customerAddress'
import { MagentoCountryCodeEnum } from 'lib/types'

interface Props {
  address: CustomerAddress
  addressType?: string
}

declare type CountryCode = keyof typeof MagentoCountryCodeEnum

export const Item: React.FC<Props> = ({ address, addressType }) => {
  const {
    id,
    isDefaultShipping,
    isDefaultBilling
  } = address ?? {}
  const { addresses } = useCustomer()
  const { updateCheckoutAddress } = useSetDefaultShippingAddressOnCart()
  const currentAddress = addresses.find(address => address.id === id)

  const { deleteCustomerAddress, isLoading: isDeletingAddress } = useDeleteCustomerAddress({
    customerAddressId: id,
    onSuccess: () => {
      Toast.toastSuccess({ messageId: 'customer.account.address.add.success' })
    },
    onError: () => {
      Toast.toastFailure()
    }
  })

  const handleDelete = () => {
    const confirm = window.confirm('Are you sure you want to delete the address?')
    if (confirm) {
      deleteCustomerAddress()
    }
  }

  const { updateDefaultShipping, isLoading: isUpdateDefaultShippingLoading } = useUpdateDefaultShipping({
    onSuccess: () => {
      Toast.toastSuccess({ messageId: 'customer.account.address.update.default.shipping' })
      currentAddress && updateCheckoutAddress({
        ...currentAddress,
        region: '',
        regionId: 0,
        countryCode: MagentoCountryCodeEnum[currentAddress.countryCode as CountryCode]
      })
    },
    onError: () => {
      Toast.toastFailure()
    }
  })

  const { updateDefaultBilling, isLoading: isUpdateDefaultBillingLoading } = useUpdateDefaultBilling({
    onSuccess: () => {
      Toast.toastSuccess({ messageId: 'customer.account.address.update.default.billing' })
    },
    onError: () => {
      Toast.toastFailure()
    }
  })

  let cardTitle
  switch (addressType) {
    case 'defaultBilling':
      cardTitle = 'Default Billing Address'
      break
    case 'defaultShipping':
      cardTitle = 'Default Shipping Address'
      break
    default:
      cardTitle = undefined
      break
  }

  return (
    <>
      {cardTitle && (
        <p><strong>{cardTitle}</strong></p>
      )}
      <div className={`card ${(isDefaultShipping || isDefaultBilling) && 'default'}`}>
        <AddressInfo address={address} />
        <AddressActions
          address={address}
          handleDelete={handleDelete}
          onDefaultBillingChange={() => updateDefaultBilling(address.id, !isDefaultBilling)}
          onDefaultShippingChange={() => updateDefaultShipping(address.id, !isDefaultShipping)}
          isDeleting={isDeletingAddress}
          isFetching={isUpdateDefaultShippingLoading || isUpdateDefaultBillingLoading}
        />
      </div>
    </>
  )
}
